import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';
import './GardenAuth.css';
import photoboothIcon from '../img/photobooth22.png';
import guestbookIcon from '../img/guestbook.png';
import gardenIcon from '../img/garden.png';
import orbitrunIcon from '../img/orbitrun.png';
import musicPlayerIcon from '../img/cd-icon.png';
import geniusIcon from '../img/genius.png';

const API_URL = process.env.REACT_APP_API_URL || 'https://moodboards-b8265.web.app';

interface LoginSignupWindowProps {
  onClose: () => void;
}

const MarqueeContent: React.FC = () => {
  return (
    <>
      <span className="marquee-item">
        <img src={photoboothIcon} alt="" className="marquee-icon" />
        SNAP A PHOTO IN PHOTOBOOTH
      </span>
      <span className="marquee-separator"> / </span>
      <span className="marquee-item">
        <img src={guestbookIcon} alt="" className="marquee-icon" />
        SIGN THE GUESTBOOK
      </span>
      <span className="marquee-separator"> / </span>
      <span className="marquee-item">
        <img src={gardenIcon} alt="" className="marquee-icon" />
        PLANT SEEDS IN YOUR GARDEN
      </span>
      <span className="marquee-separator"> / </span>
      <span className="marquee-item">
        <img src={orbitrunIcon} alt="" className="marquee-icon" />
        BEAT @VVS'S HIGH SCORE OF 1009
      </span>
      <span className="marquee-separator"> / </span>
      <span className="marquee-item">
        <img src={musicPlayerIcon} alt="" className="marquee-icon" />
        SAVE YOUR FAVORITE MUSIC
      </span>
      <span className="marquee-separator"> / </span>
      <span className="marquee-item">
        <img src={geniusIcon} alt="" className="marquee-icon" />
        ANNOTATE LYRICS, VOTE ON OTHERS
      </span>
      <span className="marquee-separator"> / </span>
      <span className="marquee-item">ORBIT OS</span>
      <span className="marquee-separator"> / </span>
      <span className="marquee-item">THINK LESS, DO MORE</span>
      <span className="marquee-separator"> / </span>
    </>
  );
};

const LoginSignupWindow: React.FC<LoginSignupWindowProps> = ({ onClose }) => {
  const [mode, setMode] = useState<'welcome' | 'login' | 'signup'>('welcome');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [handle, setHandle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, register, setHandle: setAuthHandle, currentUser } = useAuth();

  // Auto-close after successful login/signup
  useEffect(() => {
    if (currentUser) {
      // Give a brief moment for the user to see they're logged in
      setTimeout(() => {
        onClose();
      }, 300);
    }
  }, [currentUser, onClose]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // onClose will be called automatically by useEffect when currentUser updates
    } catch (error: any) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Check if handle is available
      const handleAvailable = await checkHandleIsFree();

      if (!handleAvailable) {
        setError('Handle already exists. Please pick a different handle');
        setLoading(false);
        return;
      }

      // Register user with Firebase
      await register(email, password);

      // Create handle mapping
      await createUserHandle();

      // Set handle in AuthContext for immediate access
      setAuthHandle(handle);

      // onClose will be called automatically by useEffect when currentUser updates

    } catch (error: any) {
      setError(error.message);
      setLoading(false);
    }
  };

  const checkHandleIsFree = async (): Promise<boolean> => {
    try {
      const res = await fetch(`${API_URL}/checkHandleIsFree/${handle}`);
      const resText = await res.text();
      return resText === "handle is free";
    } catch (error) {
      console.error('Error checking handle availability:', error);
      setError('Error checking handle availability');
      return false;
    }
  };

  const createUserHandle = async () => {
    if (!auth.currentUser?.uid) {
      throw new Error('No authenticated user found');
    }

    try {
      const reqBody = {
        userId: auth.currentUser.uid,
        handle: handle,
        auth: auth,
      };

      const reqOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqBody)
      };

      await fetch(`${API_URL}/createHandle`, reqOptions);
    } catch (error) {
      console.error('Error creating handle:', error);
      throw error;
    }
  };

  if (mode === 'welcome') {
    return (
      <div className="garden-auth">
        <div className="marquee-container marquee-top">
          <div className="marquee-content marquee-left">
            <MarqueeContent />
            <MarqueeContent />
            <MarqueeContent />
          </div>
        </div>
        <div className="garden-auth-welcome">
          <img src={require('../img/logo3.png')} alt="orbitOS logo" className="garden-auth-logo" />
          <h1>welcome to orbitOS</h1>
          <p>Create an account to interact with orbitOS apps</p>
          <div className="garden-auth-buttons">
            <button
              className="garden-auth-button garden-auth-login"
              onClick={() => setMode('login')}
            >
              Login
            </button>
            <button
              className="garden-auth-button garden-auth-signup"
              onClick={() => setMode('signup')}
            >
              Sign Up
            </button>
          </div>
        </div>
        <div className="marquee-container marquee-bottom">
          <div className="marquee-content marquee-right">
            <MarqueeContent />
            <MarqueeContent />
            <MarqueeContent />
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'login') {
    return (
      <div className="garden-auth">
        <div className="marquee-container marquee-top">
          <div className="marquee-content marquee-left">
            <MarqueeContent />
            <MarqueeContent />
            <MarqueeContent />
          </div>
        </div>
        <div className="garden-auth-form">
          <h2>Login to orbitOS</h2>

          {error && <div className="garden-auth-error">{error}</div>}

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="garden-auth-submit"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="garden-auth-switch">
            <button
              type="button"
              onClick={() => setMode('welcome')}
              className="garden-auth-back"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => setMode('signup')}
              className="garden-auth-link"
            >
              Don't have an account? Sign up
            </button>
          </div>
        </div>
        <div className="marquee-container marquee-bottom">
          <div className="marquee-content marquee-right">
            <MarqueeContent />
            <MarqueeContent />
            <MarqueeContent />
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'signup') {
    return (
      <div className="garden-auth">
        <div className="marquee-container marquee-top">
          <div className="marquee-content marquee-left">
            <MarqueeContent />
            <MarqueeContent />
            <MarqueeContent />
          </div>
        </div>
        <div className="garden-auth-form">
          <h2>Sign Up for orbitOS</h2>
          <p className="garden-auth-subtitle">Your handle will be used as: @{handle}</p>

          {error && <div className="garden-auth-error">{error}</div>}

          <form onSubmit={handleSignup}>
            <div className="form-group">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <input
                type="text"
                placeholder="Handle"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="garden-auth-submit"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>

          <div className="garden-auth-switch">
            <button
              type="button"
              onClick={() => setMode('welcome')}
              className="garden-auth-back"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => setMode('login')}
              className="garden-auth-link"
            >
              Already have an account? Login
            </button>
          </div>
        </div>
        <div className="marquee-container marquee-bottom">
          <div className="marquee-content marquee-right">
            <MarqueeContent />
            <MarqueeContent />
            <MarqueeContent />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default LoginSignupWindow;
